import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { FontAwesomeIcon } from '@hivebrite/design-system/FontAwesomeIcon';
import { FakeLink } from '@hivebrite/design-system/Link';
import { ScreenReadersContent } from '@hivebrite/design-system/ScreenReadersContent';

import { VerticalDivider } from './VerticalDivider';
import { vote } from '../utils/actions';

const STATE_UP = 'up';
const STATE_DOWN = 'down';

export default ({ onVote, voteableId, voteableType, votes, onSuccess }) => {
  const [score, setScore] = useState(votes.score);
  const [state, setState] = useState(votes.state);
  const [isDraft, setIsDraft] = useState(false);

  const onVoteSuccess = useCallback(
    (vote) => {
      const { score, state } = vote;

      setState(state);
      setScore(score);
      setIsDraft(false);
      if (onSuccess) {
        onSuccess(state);
      }

      onVote({
        commentId: voteableId,
        state,
        score,
      });
    },
    [voteableId, setState, setScore, setIsDraft, onVote, onSuccess],
  );

  const onVoteError = useCallback(() => {
    setState(votes.state);
    setScore(votes.score);
    setIsDraft(false);
  }, [votes, setState, setScore, setIsDraft]);

  const handleVoteUp = useCallback(() => {
    const anotherScore = state === STATE_DOWN ? score + 2 : votes.score + 1;
    const draftVote = {
      state: state === STATE_UP ? null : STATE_UP,
      score: state === STATE_UP ? score - 1 : anotherScore,
    };

    setState(draftVote.state);
    setScore(draftVote.score);
    setIsDraft(true);

    vote({
      voteableId,
      voteableType,
      onResponse: onVoteSuccess,
      onError: onVoteError,
      state: 1,
    });
  }, [
    votes,
    state,
    score,
    setState,
    setScore,
    setIsDraft,
    voteableId,
    voteableType,
    onVoteSuccess,
    onVoteError,
  ]);

  const handleVoteDown = useCallback(() => {
    const anotherScore = state === STATE_UP ? score - 2 : votes.score - 1;
    const draftVote = {
      state: state === STATE_DOWN ? null : STATE_DOWN,
      score: state === STATE_DOWN ? score + 1 : anotherScore,
    };

    setState(draftVote.state);
    setScore(draftVote.score);
    setIsDraft(true);

    vote({
      voteableId,
      voteableType,
      onResponse: onVoteSuccess,
      onError: onVoteError,
      state: -1,
    });
  }, [
    votes,
    state,
    score,
    setState,
    setScore,
    setIsDraft,
    voteableId,
    voteableType,
    onVoteSuccess,
    onVoteError,
  ]);

  useEffect(() => {
    setScore(votes.score);
    setState(votes.state);
  }, [votes]);

  return votes ? (
    <>
      <IconWrapper
        onClick={isDraft ? undefined : handleVoteUp}
        $enable={!isDraft}
      >
        <Box font="h4-regular">
          <FontAwesomeIcon
            color={state === STATE_UP && 'primary'}
            height="16px"
            iconName="up"
            iconType={state === STATE_UP ? 'solid' : 'regular'}
          />
        </Box>
        <ScreenReadersContent>{__('Arrow Up icon')}</ScreenReadersContent>
      </IconWrapper>
      <ScoreWrapper data-testid="votes-score">{score}</ScoreWrapper>
      <IconWrapper
        onClick={isDraft ? undefined : handleVoteDown}
        $enable={!isDraft}
      >
        <Box font="h4-regular">
          <FontAwesomeIcon
            color={state === STATE_DOWN && 'primary'}
            height="16px"
            iconName="down"
            iconType={state === STATE_DOWN ? 'solid' : 'regular'}
          />
        </Box>
        <ScreenReadersContent>{__('Arrow Down icon')}</ScreenReadersContent>
      </IconWrapper>
      <VerticalDivider mx="6px" />
    </>
  ) : null;
};

const IconWrapper = styled(FakeLink)`
  &:hover {
    cursor: ${({ $enable }) => ($enable ? 'pointer' : 'default')};
  }
`;

const ScoreWrapper = styled(Box).attrs(() => ({
  font: 'body-semibold-asphalt',
  fontSize: 12,
  fontWeight: '800',
  mx: '6px',
}))``;
